import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import {
  TableFilterSelect,
  InputContainer,
  Typography,
  Button,
  DatePicker,
  BelongsToFilterSelects,
} from '../../design-system';
import {
  containerConditionOptions,
  containerTypes,
  containerStatues,
  INPUT_ERRORS,
} from '../../const';
import { i18n } from '../../i18n';
import { extendContainerValidator } from '../../utils';
import { useSaveFilters, useStatesWithFilterCounter } from '../../hooks';
import { CloseIconButton, DashboardHeader } from '../dashboard';
import { useStoreUser } from '../../context';

interface FiltersPopupProps {
  isOpen: boolean;
}

const FiltersPopup = styled.div<FiltersPopupProps>`
  display: grid;
  grid-template-rows: max-content 1fr;

  height: 100%;
  width: 100%;
  background-color: #f5f6fa;

  padding: var(--spacing-3) var(--spacing-6) var(--spacing-6);
  grid-row-gap: var(--spacing-3);

  position: absolute;
  top: 0;
  right: -100%;
  transition: right 0.3s;
  z-index: 1000;

  ${(props) =>
    props.isOpen &&
    css`
      right: 0;
    `}
`;

const FiltersInputs = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-gap: var(--spacing-6);

  overflow-y: auto;
  padding-right: var(--spacing-6);
  margin-right: calc(var(--spacing-6) * -1);
`;

interface Props extends FiltersPopupProps {
  country?: string;
  city?: string;
  yardId?: string;
  clientId?: string;
  number?: string;
  type?: string;
  condition?: string;
  status?: string;
  startDate?: any;
  endDate?: any;
  setIsOpen: any;
  setStates: any;
  states: any;
}

export const MobileFilters: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  setStates,
  states,
  ...filteredStates
}: Props) => {
  const { user } = useStoreUser();

  const [oldFilters, setOldFilters] = useSaveFilters('belongs-to', true);
  const oldFiltersRef = useRef(oldFilters);
  const [filterStates, setFilterStates] = useStatesWithFilterCounter(
    {
      country: '',
      city: '',
      yardId: '',
      clientId: '',
      number: '',
      type: 'All',
      condition: 'All',
      status: 'All',
      startDate: null,
      endDate: null,
    },
    filteredStates,
  );
  const [number, setNumber] = useState('');
  const [type, setType] = useState('All');
  const [condition, setCondition] = useState('All');
  const [status, setStatus] = useState('All');
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);

  const [update, setUpdate] = useState(Math.random());

  const isFiltersChange =
    states.country !== filterStates.country ||
    states.city !== filterStates.city ||
    states.yardId !== filterStates.yardId ||
    states.clientId !== filterStates.clientId ||
    states.number !== number ||
    states.type !== type ||
    states.condition !== condition ||
    states.status !== status ||
    states.startDate !== startDate ||
    states.endDate !== endDate;

  useEffect(() => {
    if (!isOpen) return;

    const oldFiltersData = {
      ...(filteredStates.country ? { country: filteredStates.country } : {}),
      ...(filteredStates.city ? { city: filteredStates.city } : {}),
      ...(filteredStates.yardId
        ? {
            yardId: filteredStates.yardId,
            yardName: oldFilters.yardName || oldFiltersRef.current.yardName,
          }
        : {}),
      ...(filteredStates.clientId
        ? {
            clientId: filteredStates.clientId,
            clientName: oldFilters.clientName || oldFiltersRef.current.clientName,
          }
        : {}),
    };
    setOldFilters(oldFiltersData);
    oldFiltersRef.current = oldFiltersData;
    setFilterStates('force', { ...states, ...filteredStates });
    setNumber(filteredStates.number || '');
    setType(filteredStates.type || 'All');
    setCondition(filteredStates.condition || 'All');
    setStatus(filteredStates.status || 'All');
    setStartDate(filteredStates.startDate || null);
    setEndDate(filteredStates.endDate || null);

    setUpdate(Math.random());
  }, [isOpen]);

  const handleApplyFiltersClick = () => {
    setStates('force', {
      country: filterStates.country,
      city: filterStates.city,
      yardId: filterStates.yardId,
      clientId: filterStates.clientId,
      number,
      type,
      condition,
      status,
      startDate,
      endDate,
    });
    setIsOpen(!isOpen);
  };

  return (
    <FiltersPopup isOpen={isOpen}>
      <DashboardHeader isSmallScreen={true}>
        <Typography variant={'textL'} weight={'bold'}>
          {i18n.t('pages.marketplace.filters')}
        </Typography>
        <CloseIconButton onClick={() => setIsOpen(false)} />
      </DashboardHeader>
      <FiltersInputs>
        <BelongsToFilterSelects
          key={update}
          handleChange={(obj: any) => {
            setFilterStates('force', obj);
          }}
        />
        <InputContainer
          label={i18n.t('label.containerNumber')}
          data-size={'medium'}
          value={number}
          onChange={(e) => {
            const value = e.target.value.toUpperCase();
            setNumber(value);
          }}
          helperText={
            number.length !== 0 && number.length < 3
              ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER_MIN
              : !extendContainerValidator(number)
              ? INPUT_ERRORS.NOT_VALID.CONTAINER_NUMBER_SHORT
              : ''
          }
          alwaysShowHelperText
        />
        <TableFilterSelect
          label={i18n.t('label.type')}
          options={containerTypes}
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
        <TableFilterSelect
          label={i18n.t('label.condition')}
          options={containerConditionOptions}
          value={condition}
          onChange={(e) => setCondition(e.target.value)}
        />
        <TableFilterSelect
          label={i18n.t('label.status')}
          options={containerStatues(user.canTrade)}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            if (e.target.value !== 'GATED_IN' && e.target.value !== 'GATED_OUT') {
              setStartDate(null);
              setEndDate(null);
            }
          }}
        />
        {(status === 'GATED_IN' || status === 'GATED_OUT') && (
          <>
            <DatePicker
              label={i18n.t('label.from')}
              data-size={'medium'}
              value={startDate}
              onChange={(e) => setStartDate(e)}
              maxDate={endDate ? moment(endDate) : moment()}
            />
            <DatePicker
              label={i18n.t('label.to')}
              data-size={'medium'}
              value={endDate}
              onChange={(e) => setEndDate(e)}
              minDate={startDate ? moment(startDate) : null}
              maxDate={moment()}
            />
          </>
        )}
        <Button size={'medium'} onClick={handleApplyFiltersClick} disabled={!isFiltersChange}>
          {i18n.t('button.applyFilters')}
        </Button>
      </FiltersInputs>
    </FiltersPopup>
  );
};
